<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AiAgentTemplatesAPI from 'dashboard/api/aiAgents/templates';

const router = useRouter();
const { t } = useI18n();

const templates = ref([]);
const isLoading = ref(true);

const loadTemplates = async () => {
  try {
    isLoading.value = true;
    const { data } = await AiAgentTemplatesAPI.get();
    templates.value = data || [];
  } catch {
    useAlert(t('AI_AGENTS.TEMPLATES_PAGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openScratch = () => {
  router.push({ name: 'ai_agents_new' });
};

const openTemplate = template => {
  router.push({
    name: 'ai_agents_template_preview',
    params: { slug: template.slug },
  });
};

const goBack = () => {
  router.push({ name: 'ai_agents' });
};

onMounted(loadTemplates);
</script>

<template>
  <SettingsLayout>
    <div class="pt-4 md:pt-6">
      <BaseSettingsHeader
        :title="t('AI_AGENTS.TEMPLATES_PAGE.TITLE')"
        :description="t('AI_AGENTS.TEMPLATES_PAGE.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="t('AI_AGENTS.ACTIONS.BACK')"
            icon="i-lucide-arrow-left"
            slate
            faded
            sm
            @click="goBack"
          />
        </template>
      </BaseSettingsHeader>

      <div class="px-6 pb-8 pt-6 md:pt-8">
        <p v-if="isLoading" class="text-sm text-n-slate-11">
          {{ t('AI_AGENTS.TEMPLATES_PAGE.LOADING') }}
        </p>

        <div
          v-else
          class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <button
            type="button"
            class="flex min-h-[220px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak bg-n-background px-6 py-8 text-center transition-colors hover:border-n-brand hover:bg-n-alpha-black2"
            @click="openScratch"
          >
            <span class="text-3xl" aria-hidden="true">{{
              t('AI_AGENTS.TEMPLATES_PAGE.FROM_SCRATCH.ICON')
            }}</span>
            <h3 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_AGENTS.TEMPLATES_PAGE.FROM_SCRATCH.TITLE') }}
            </h3>
            <p class="m-0 max-w-xs text-sm text-n-slate-11">
              {{ t('AI_AGENTS.TEMPLATES_PAGE.FROM_SCRATCH.DESCRIPTION') }}
            </p>
          </button>

          <button
            v-for="template in templates"
            :key="template.id"
            type="button"
            class="flex min-h-[220px] flex-col items-start gap-3 rounded-xl border border-n-weak bg-n-background px-6 py-8 text-left transition-colors hover:border-n-brand hover:bg-n-alpha-black2"
            @click="openTemplate(template)"
          >
            <span class="text-3xl" aria-hidden="true">{{
              template.emoji
            }}</span>
            <h3 class="m-0 text-base font-semibold text-n-slate-12">
              {{ template.name }}
            </h3>
            <p class="m-0 flex-1 text-sm text-n-slate-11 line-clamp-4">
              {{
                template.description ||
                t('AI_AGENTS.TEMPLATES_PAGE.NO_DESCRIPTION')
              }}
            </p>
            <span class="text-xs font-medium text-n-brand">
              {{ t('AI_AGENTS.TEMPLATES_PAGE.USE_TEMPLATE') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </SettingsLayout>
</template>
